import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

interface Props {
  src: string;
  alt?: string;
  layoutId: string;
  isOpen: boolean;
  onClose: () => void;
  // Fires once AnimatePresence has finished tearing down the lightbox subtree
  // (including the shared-layout morph back to the thumb). Callers can use
  // this to remount the thumb element and reset framer-motion's projection
  // cache for the shared layoutId — otherwise cached state on the long-lived
  // thumb drifts between open/close cycles and causes a flicker on close.
  onExitComplete?: () => void;
}

export function ImageLightbox({ src, alt, layoutId, isOpen, onClose, onExitComplete }: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Close button */}
          <motion.button
            className="fixed top-4 right-4 z-[52] p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, delay: 0.05 }}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </motion.button>

          {/* Wrapper animates from thumbnail via layoutId; image sizes to its
              natural aspect ratio so the full image is revealed. */}
          <div className="fixed inset-0 z-[51] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              layoutId={layoutId}
              className="overflow-hidden rounded-2xl shadow-2xl pointer-events-auto"
              style={{ maxWidth: "min(92vw, 900px)" }}
              transition={{ type: "spring", stiffness: 340, damping: 32 }}
            >
              <img
                src={src}
                alt={alt ?? ""}
                className="block w-full h-auto"
                style={{ maxHeight: "92vh" }}
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
